import React, { createContext, useContext, useState, PropsWithChildren } from 'react';
import { AlertColor, Snackbar, Alert } from '@mui/material';
import apiService from '../services/api';

export interface NotificationOptions {
  message: string;
  severity?: AlertColor;
  source?: string;
  bookingId?: number;
  metadata?: Record<string, unknown>;
}

interface NotificationContextValue {
  notify: (options: NotificationOptions) => void;
}

const NotificationContext = createContext<NotificationContextValue>({
  notify: () => undefined,
});

export const NotificationProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState<AlertColor>('info');

  const notify = ({ message, severity = 'info', source, bookingId, metadata }: NotificationOptions) => {
    setMessage(message);
    setSeverity(severity);
    setOpen(true);
    persistLog({ message, severity, source, bookingId, metadata });
  };

  const persistLog = async (entry: NotificationOptions) => {
    try {
      await apiService.logAdminActivity({
        message: entry.message,
        severity: entry.severity || 'info',
        source: entry.source,
        booking_id: entry.bookingId,
        metadata: entry.metadata,
      });
    } catch (error) {
      console.error('Failed to persist admin activity log:', error);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <NotificationContext.Provider value={{ notify }}>
      {children}
      <Snackbar open={open} autoHideDuration={4000} onClose={handleClose} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity={severity} onClose={handleClose} sx={{ width: '100%' }}>
          {message}
        </Alert>
      </Snackbar>
    </NotificationContext.Provider>
  );
};

export const useNotification = () => useContext(NotificationContext);
